import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ethers } from "ethers";
import FlowModal from "../components/FlowModal.jsx";
import ResultModal from "../components/ResultModal.jsx";
import RepayChoiceModal from "../components/RepayChoiceModal.jsx";
import LtvBadge from "../components/LtvBadge.jsx";
import { StatCard, STAT_ICONS } from "../components/StatRow.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useDeposits } from "../hooks/useDeposits.js";
import { useLoansByOwner } from "../hooks/useLoans.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { decodeContractError } from "../utils/contractError.js";
import { formatUsdcCompact, liveLoanInterest } from "../utils/format.js";
import { chainNowSec } from "../utils/chainClock.js";

const TABS = [
    { id: "active", label: "Active" },
    { id: "closed", label: "Closed" }
];

// §V6.2 — per-product Safe Vault. Standard 80%, Turbo 50%. Prefers
// the chain-stored `safeVaultAmount` (immutable at deposit open) so
// the figure matches the contract exactly; falls back to a
// productMode-derived bps when the indexer hasn't synced the field
// yet. Bare-`config.protocol.safeVaultBps` would mis-quote every
// Turbo loan (Standard 80% × Turbo $1001 = $800.80 instead of the
// real $500.50), inflating remaining capacity and under-reporting
// LTV.
function rowSafeVault(row) {
    if (row?.safeVaultAmount && BigInt(row.safeVaultAmount) > 0n) {
        return BigInt(row.safeVaultAmount);
    }
    const principal = BigInt(row?.principal || 0);
    const bps = Number(row?.productMode) === 1 ? 5000n : 8000n;
    return (principal * bps) / 10000n;
}

// LTV computed from `loanBalance / safeVault`. We surface the same band
// thresholds the LtvBadge uses so the filter chip matches the row badge
// the user is reading on screen.
function computeLtvPct(row) {
    const safeVault = rowSafeVault(row);
    if (safeVault === 0n) return 0;
    return Math.round(Number((row.loanBalance * 10000n) / safeVault) / 100);
}

const LTV_FILTERS = [
    { id: "all", label: "All LTV", match: () => true },
    { id: "safe", label: "Safe (≤ 50%)", match: (r) => computeLtvPct(r) <= 50 },
    { id: "watch", label: "Watch (51–75%)", match: (r) => {
        const pct = computeLtvPct(r);
        return pct > 50 && pct <= 75;
    } },
    { id: "risky", label: "Risky (> 75%)", match: (r) => computeLtvPct(r) > 75 }
];

function bigCmp(a, b) {
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
}

const LOAN_SORT_OPTIONS = [
    { id: "newest", label: "Newest first", compare: (a, b) => Number(b.id) - Number(a.id) },
    { id: "oldest", label: "Oldest first", compare: (a, b) => Number(a.id) - Number(b.id) },
    { id: "payable_desc", label: "Largest payable", compare: (a, b) => bigCmp(b.loanBalance, a.loanBalance) },
    { id: "payable_asc", label: "Smallest payable", compare: (a, b) => bigCmp(a.loanBalance, b.loanBalance) },
    { id: "ltv_desc", label: "Highest LTV", compare: (a, b) => computeLtvPct(b) - computeLtvPct(a) },
    { id: "ltv_asc", label: "Lowest LTV", compare: (a, b) => computeLtvPct(a) - computeLtvPct(b) }
];

/**
 * Loans page. Top stat strip mirrors the Dashboard / My TDs styling
 * (yellow accent on the figure, tiny info-icon, green delta) but the
 * labels track loan-specific quantities (Total Payable, Loan Interest
 * Cost, Avg LTV, Active Loans). Below: tab strip + filterable table of
 * each open loan with an LTV progress bar and per-row Repay action.
 *
 * Data comes straight from the on-chain `useDeposits` hook —
 * deposits with a non-zero loanBalance count as active loans.
 */
export default function MyLoans() {
    const { address, signer } = useWallet();
    const { deposits, isLoading, refresh } = useDeposits(address);
    // Pull the full loan history (open + closed) from the backend so the
    // Closed tab can show repaid / liquidated loans that no longer have
    // a non-zero loanBalance on their Deposit row.
    const { loans: historyLoans, isLoading: historyLoading, refresh: refreshHistory } =
        useLoansByOwner(address, { includeClosed: true });
    const [tab, setTab] = useState("active");
    const [search, setSearch] = useState("");
    const [sortKey, setSortKey] = useState("newest");
    const [filterLtv, setFilterLtv] = useState("all");
    const [repayTarget, setRepayTarget] = useState(null);
    const [flowOpen, setFlowOpen] = useState(false);
    const [steps, setSteps] = useState([]);
    const [result, setResult] = useState(null);
    // Repay-choice modal state. `repayChoice` holds the loan being repaid
    // plus the page's `liveLoan` snapshot so the modal's displayed
    // interest matches the row behind it. null = modal closed.
    const [repayChoice, setRepayChoice] = useState(null);

    // Active loans = current deposits with an outstanding loanBalance.
    // We keep this chain-derived (not from the indexer) so the row shows
    // the latest balance even before the next indexer tick. Live accrued
    // interest is overlaid from the loan record (which the backend keeps
    // up-to-the-second via lending.controller.js → getByDeposit) so the
    // Interest column shows what the borrower ACTUALLY owes right now,
    // not a full-year projection.
    const loanByDeposit = useMemo(() => {
        const map = new Map();
        for (const l of historyLoans) {
            if (l.isOpen !== false) map.set(l.depositId, l);
        }
        return map;
    }, [historyLoans]);
    // 1-second ticker so the live accrued-interest figures (headline
    // strip + per-row Interest column) advance visibly between the
    // 15s API polls — without this the page kept showing the value
    // from the last fetch while the repay modal (which reads chain
    // fresh on click) showed a noticeably larger number, making the
    // two views appear to disagree.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    const activeLoans = useMemo(
        () => deposits.filter((d) => BigInt(d.loanBalance) > 0n).map((d) => {
            // §V6.2 — synthesize a loan record from the deposit row
            // when the backend hasn't indexed the matching Loan
            // document yet. The Deposit struct mirrors loanBalance +
            // loanAccruedInterest via setLoanState, so we have enough
            // to drive the live tick — even without the full Loan
            // record. `liveLoanInterest`'s own missing-fields
            // fallbacks (default aprBps, openedAt → 1min ago) take
            // it from there. Earlier this code set accrued = 0n
            // whenever `live` was missing, freezing the Loan Interest
            // Cost card whenever the loan was very fresh OR opened
            // by CYR (a path whose initial state-changed event hasn't
            // hit the backend yet).
            const live = loanByDeposit.get(d.id) || {
                principal: BigInt(d.loanBalance),
                storedAccruedInterest: BigInt(d.loanAccruedInterest || 0)
                // aprBps + lastAccruedAt deliberately omitted — let
                // liveLoanInterest apply its own defaults.
            };
            // Live accrual: stored value (frozen at last on-chain event)
            // + per-second extrapolation since `lastAccruedAt`. Uses the
            // tick value as a dependency so this recomputes every second.
            const accrued = liveLoanInterest(live); // chain-time default
            return {
                id: d.id,
                isClosedRow: false,
                principal: BigInt(d.principal),
                loanBalance: BigInt(d.loanBalance),
                accruedInterest: accrued,
                liveDebt: BigInt(d.loanBalance) + accrued,
                txHash: d.txHash || "",
                state: Number(d.state),
                // §V6.2 — carry the product fields so rowSafeVault()
                // can pick the right Safe Vault %. Without these the
                // helper falls back to 80% for every loan (which
                // mis-quotes the Turbo positions visible on this page).
                productMode: Number(d.productMode || 0),
                safeVaultAmount: d.safeVaultAmount || "0",
                // Carry maturity so the LTV badge can colour by term status:
                // green in-term, orange/red only once the term has ended
                // (liquidation can't fire before maturity).
                maturityTime: Number(d.maturityTime || 0)
            };
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [deposits, loanByDeposit, tick]
    );

    // Closed loans = backend Loan records that have isOpen=false. The
    // borrow amount is preserved as `originalPrincipal` so we can still
    // show "Borrowed $X" even though current principal is now 0. We
    // also enrich with the deposit's principal (for context) when the
    // Deposit row still exists, falling back to a derived value when
    // the deposit itself has been settled/exited.
    const closedLoans = useMemo(() => {
        const depositById = new Map(deposits.map((d) => [d.id, d]));
        return historyLoans
            .filter((l) => !l.isOpen)
            .map((l) => {
                const dep = depositById.get(l.depositId);
                return {
                    id: l.depositId,
                    isClosedRow: true,
                    principal: dep ? BigInt(dep.principal) : 0n,
                    loanBalance: l.originalPrincipal,
                    totalInterestPaid: l.totalInterestPaid,
                    closedAt: l.closedAt,
                    liquidatedAt: l.liquidatedAt,
                    txHash: "",
                    state: 6,
                    // §V6.2 — carry product fields for consistency with
                    // active rows. Closed rows don't render LTV (they
                    // show "Closed" pill), but the sort/filter helpers
                    // still touch rowSafeVault() and need accurate bps.
                    productMode: dep ? Number(dep.productMode || 0) : 0,
                    safeVaultAmount: dep ? dep.safeVaultAmount || "0" : "0"
                };
            });
    }, [historyLoans, deposits]);

    /**
     * Step 1 of repay: opens the choice modal sourced from the same
     * normalised loan record the table row is rendering — so the modal's
     * Interest line is identical to the row behind it instead of
     * diverging because the modal did its own chain read. `repayFull`
     * recomputes the exact debt on-chain at execution time, so any
     * few-second drift between display and block time is absorbed by
     * the contract.
     */
    function openRepayChoice(loan) {
        if (!signer) return;
        const liveLoan = loanByDeposit.get(loan.id);
        if (!liveLoan) {
            setResult({
                type: "error",
                title: "Loan not loaded",
                message: "Try again in a moment."
            });
            return;
        }
        const principal = liveLoan.principal;
        const accruedInterest = liveLoanInterest(liveLoan); // chain-time default
        if (principal + accruedInterest === 0n) {
            setResult({
                type: "error",
                title: "Nothing to repay",
                message: "This loan is already closed."
            });
            return;
        }
        setRepayChoice({
            loan,
            principal,
            accruedInterest,
            accrual: liveLoan
        });
    }

    /**
     * Step 2 of repay: runs the actual approve + repay transaction with
     * `amountWei` chosen in the modal. `isFull` routes to `repayFull`
     * (re-reads the debt inside the tx so no dust is left); a partial
     * amount routes to `repayLoan(depositId, amount)` and leaves the
     * remaining balance open for a later payment.
     */
    async function runRepay(loan, amountWei, isFull) {
        if (!signer || amountWei <= 0n) return;
        setRepayChoice(null);
        setRepayTarget({ loan, debt: amountWei });
        setSteps([
            { key: "approve", label: `Approve ${formatUsdcCompact(amountWei)} USDC`, status: "loading" },
            { key: "repay", label: isFull ? "Full Settlement" : "Pay Now", status: "pending" }
        ]);
        setFlowOpen(true);
        setResult(null);

        try {
            const lending = getContract("lending", signer);
            const overrides = await getTxOverrides(signer.provider);

            // Ask Lending directly which USDC contract it'll pull from,
            // instead of trusting VITE_USDC_ADDRESS — those two can drift
            // apart on independent redeploys and a stale env value would
            // leave the user approving the WRONG token (→ MockUSDC:
            // allowance at repay time).
            const assetAddress = await lending.asset();
            const usdc = new ethers.Contract(
                assetAddress,
                ["function allowance(address,address) view returns (uint256)", "function approve(address,uint256) returns (bool)"],
                signer
            );

            // MaxUint256 approval so the tx never reverts on an allowance
            // gap caused by between-block interest accrual.
            const ownerAddr = await signer.getAddress();
            const allowance = await usdc.allowance(ownerAddr, config.contracts.lending);
            if (allowance < amountWei) {
                const approveTx = await usdc.approve(config.contracts.lending, ethers.MaxUint256, overrides);
                await approveTx.wait();
                // Race guard: `approveTx.wait()` only means the dApp's
                // provider saw the approval — the RPC the WALLET uses to
                // simulate (eth_estimateGas) the repay can still be a
                // block behind, reading allowance as 0 and reverting with
                // "MockUSDC: allowance". Poll until the approval is
                // actually visible before sending the repay.
                await waitForAllowance(usdc, ownerAddr, config.contracts.lending, amountWei);
            }
            setSteps((prev) => prev.map((s) =>
                s.key === "approve" ? { ...s, status: "done" } : s.key === "repay" ? { ...s, status: "loading" } : s
            ));

            const tx = isFull
                ? await lending.repayFull(loan.id, overrides)
                : await lending.repayLoan(loan.id, amountWei, overrides);
            await tx.wait();
            setSteps((prev) => prev.map((s) => s.key === "repay" ? { ...s, status: "done" } : s));

            await new Promise((r) => setTimeout(r, 300));
            setFlowOpen(false);
            setResult(
                isFull
                    ? {
                          type: "success",
                          title: "Loan Closed",
                          message: `Paid off Term Deposit #${loan.id}'s loan in full — nothing left outstanding.`
                      }
                    : {
                          type: "success",
                          title: "Payment Applied",
                          message: `Repaid ${formatUsdcCompact(amountWei)} USDC against Term Deposit #${loan.id}'s loan. You can clear the remaining balance anytime.`
                      }
            );
            await Promise.all([refresh(), refreshHistory()]);
        } catch (err) {
            setSteps((prev) => prev.map((s) => s.status === "loading" ? { ...s, status: "error" } : s));
            await new Promise((r) => setTimeout(r, 300));
            setFlowOpen(false);
            setResult({
                type: "error",
                title: "Pay Failed",
                message: decodeContractError(err)
            });
        }
    }

    // Stat strip aggregates apply to ACTIVE loans only — the closed
    // ones have no outstanding payable. Interest is LIVE accrued (from
    const totals = useMemo(() => {
        let totalPayable = 0n;
        let totalInterest = 0n;
        let totalRem = 0n;
        for (const d of activeLoans) {
            const principalLoan = d.loanBalance;
            const interest = d.accruedInterest;
            // §V6.2 — per-product Safe Vault via rowSafeVault().
            const safeVault = rowSafeVault(d);
            const maxBorrow = (safeVault * BigInt(config.protocol.maxLtvBps)) / 10000n;
            const rem = maxBorrow - principalLoan;
            totalPayable += principalLoan + interest;
            totalInterest += interest;
            totalRem += rem > 0n ? rem : 0n;
        }
        return { totalPayable, totalInterest, totalRem };
    }, [activeLoans]);

    const avgLtv = useMemo(() => {
        if (activeLoans.length === 0) return 0;
        let sum = 0;
        for (const d of activeLoans) {
            const safe = rowSafeVault(d);
            if (safe === 0n) continue;
            const ltvBps = Number((d.loanBalance * 10000n) / safe);
            sum += ltvBps;
        }
        return Math.round(sum / activeLoans.length / 100); // bps → %
    }, [activeLoans]);

    const counts = useMemo(
        () => ({ active: activeLoans.length, closed: closedLoans.length }),
        [activeLoans, closedLoans]
    );

    const rows = useMemo(() => {
        const source = tab === "closed" ? closedLoans : activeLoans;
        const ltv = LTV_FILTERS.find((o) => o.id === filterLtv) || LTV_FILTERS[0];
        const sort = LOAN_SORT_OPTIONS.find((o) => o.id === sortKey) || LOAN_SORT_OPTIONS[0];
        const q = search.trim().toLowerCase();
        const filtered = source.filter((row) => {
            if (q && !String(row.id).includes(q)) return false;
            // LTV filter only meaningful on active loans; closed loans
            // have nothing to liquidate so we let them all through.
            if (tab === "active" && !ltv.match(row)) return false;
            return true;
        });
        return filtered.slice().sort(sort.compare);
    }, [activeLoans, closedLoans, tab, search, sortKey, filterLtv]);

    function clearLoanFilters() {
        setFilterLtv("all");
    }

    const loanFiltersApplied = filterLtv !== "all";

    return (
        <div className="-mt-6 space-y-5">
            <LoansStatRow totals={totals} avgLtv={avgLtv} activeCount={counts.active} />

            <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-manrope text-[20px] font-semibold text-[#b98c23] sm:text-[22px]">My Loans</h2>
                <BorrowNewLoanMenu />
            </div>

            <TabStrip
                tab={tab}
                setTab={setTab}
                counts={counts}
                search={search}
                setSearch={setSearch}
                sortKey={sortKey}
                setSortKey={setSortKey}
                filterLtv={filterLtv}
                setFilterLtv={setFilterLtv}
                filtersApplied={loanFiltersApplied}
                clearFilters={clearLoanFilters}
            />

            <LoansTable rows={rows} isLoading={tab === "closed" ? historyLoading : isLoading} onRepay={openRepayChoice} />

            <RepayChoiceModal
                state={repayChoice}
                busy={flowOpen}
                onClose={() => setRepayChoice(null)}
                onConfirm={runRepay}
            />
            <FlowModal open={flowOpen} title={`Pay Loan #${repayTarget?.loan?.id || ""}`} steps={steps} />
            <ResultModal open={Boolean(result)} type={result?.type} title={result?.title} message={result?.message} onClose={() => setResult(null)} />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Create Loan CTA                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Direct link to the standard borrow flow (`/loans/lending`). Turbo is
 * still available as a separate surface but is intentionally NOT
 * exposed from this header — pick a deposit on Lending and borrow,
 * that's the canonical path. Picker can come back later if/when the
 * Lending/Turbo split is meaningful to surface again.
 */
function BorrowNewLoanMenu() {
    return (
        <Link
            to="/loans/lending"
            className="inline-flex items-center gap-2 self-start sm:self-auto rounded-full bg-[#FBC206] px-5 py-2 font-manrope text-[13px] font-bold text-[#1D3E29] hover:-translate-y-0.5 hover:shadow-md transition-all"
        >
            Create Loan
            <ChevronRight />
        </Link>
    );
}

/**
 * Polls the USDC allowance until it covers `required` — used after an
 * approve tx so the follow-up repay isn't simulated (eth_estimateGas)
 * against a stale block where the allowance still reads 0. Best-effort:
 * returns false on timeout and lets the caller proceed anyway.
 */
async function waitForAllowance(usdc, owner, spender, required, timeoutMs = 20000, intervalMs = 1000) {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        try {
            const current = await usdc.allowance(owner, spender);
            if (current >= required) return true;
        } catch {
            // node not caught up yet — keep polling
        }
        await new Promise((r) => setTimeout(r, intervalMs));
    }
    return false;
}

/* -------------------------------------------------------------------------- */
/*  Stat row                                                                  */
/* -------------------------------------------------------------------------- */

function LoansStatRow({ totals, avgLtv, activeCount }) {
    // Descriptive sub-labels — each states what its card's figure means.
    // Replaces the old hardcoded "+18.2% / +8.2%" placeholder deltas,
    // which were never real numbers.
    const loanAprPct = config.protocol.loanAprBps / 100;
    const liqPct = config.protocol.liquidationThresholdBps / 100;
    const items = [
        {
            label: "Total Payable",
            value: `$${formatUsdcCompact(totals.totalPayable)}`,
            unit: "USDC",
            delta: "Principal + interest owed",
            iconSrc: STAT_ICONS.holdings
        },
        {
            label: "Loan Interest Cost",
            value: `$${formatUsdcCompact(totals.totalInterest)}`,
            unit: "USDC",
            delta: `Accruing · ${loanAprPct}% APR`,
            iconSrc: STAT_ICONS.interest
        },
        {
            // Per client (May 2026 review): drop Safe / Watch / Risky
            // labels — "all loans are safe by definition".
            label: "Avg LTV",
            value: `${avgLtv}%`,
            unit: "",
            delta: `Liquidation at ${liqPct}%`,
            iconSrc: STAT_ICONS.safe
        },
        {
            label: "Active Loans",
            value: String(activeCount),
            unit: "",
            delta: activeCount > 0 ? "Against your Safe Vault" : "No open loans",
            iconSrc: STAT_ICONS.active
        }
    ];
    return (
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((item) => (
                <StatCard key={item.label} {...item} />
            ))}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Tab strip + filters                                                       */
/* -------------------------------------------------------------------------- */

function TabStrip({
    tab,
    setTab,
    counts,
    search,
    setSearch,
    sortKey,
    setSortKey,
    filterLtv,
    setFilterLtv,
    filtersApplied,
    clearFilters
}) {
    return (
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-10">
            <div className="inline-flex items-center rounded-full bg-[#10281b] p-0.5">
                {TABS.map((t, idx) => {
                    const active = tab === t.id;
                    const prev = idx > 0 ? TABS[idx - 1] : null;
                    const showDivider = prev && tab !== prev.id && !active;
                    return (
                        <span key={t.id} className="inline-flex items-center">
                            {showDivider ? (
                                <span className="mx-1 h-4 w-px bg-[#e6c985]/30" aria-hidden="true" />
                            ) : null}
                            <button
                                onClick={() => setTab(t.id)}
                                className={`rounded-full px-6 py-1 text-[12px] font-semibold transition-colors ${
                                    active
                                        ? "bg-[#2d3d22] text-[#FBC206]"
                                        : "text-[#e6c985] hover:text-[#FBC206]"
                                }`}
                            >
                                {t.label} ({counts[t.id]})
                            </button>
                        </span>
                    );
                })}
            </div>

            <div className="flex flex-1 flex-wrap items-center gap-2 sm:gap-3 lg:max-w-2xl lg:flex-nowrap lg:justify-end lg:gap-4">
                <div className="flex min-w-[10rem] flex-1 items-center gap-2 rounded-full bg-transparent px-3 py-1 ring-1 ring-[#bfb67b]">
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search here..."
                        className="flex-1 bg-transparent text-[12px] text-[#1D3E29] placeholder:text-[#1D3E29]/60 focus:outline-none"
                        aria-label="Search loans"
                    />
                    <SearchIcon />
                </div>
                <LoanFiltersMenu
                    tab={tab}
                    filterLtv={filterLtv}
                    setFilterLtv={setFilterLtv}
                    applied={filtersApplied}
                    onClear={clearFilters}
                />
                <LoanSortByMenu sortKey={sortKey} setSortKey={setSortKey} />
            </div>
        </div>
    );
}

/**
 * Generic outside-click + Escape handler for popovers. Returns a ref to
 * attach on the wrapper element and a state pair to control open/close.
 */
function usePopover() {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (event) => {
            if (!ref.current) return;
            if (!ref.current.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    return { open, setOpen, ref };
}

function LoanFiltersMenu({ tab, filterLtv, setFilterLtv, applied, onClear }) {
    const { open, setOpen, ref } = usePopover();
    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12px] font-semibold ring-1 transition-colors ${
                    applied
                        ? "bg-[#1D3E29] text-[#FBC206] ring-[#1D3E29]"
                        : "bg-transparent text-[#1D3E29] ring-[#bfb67b] hover:bg-[#1D3E29]/5"
                }`}
            >
                Filters
                {applied ? (
                    <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#FBC206] px-1 text-[9px] font-bold text-[#1D3E29]">
                        ON
                    </span>
                ) : (
                    <FiltersIcon />
                )}
            </button>

            {open ? (
                <div
                    role="menu"
                    className="absolute right-0 top-full z-30 mt-2 w-72 overflow-hidden rounded-[12px] bg-white ring-1 ring-[#bfb67b] shadow-xl"
                >
                    <div className="bg-[#1D3E29] px-4 py-2">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            Filters
                        </div>
                        <div className="mt-0.5 font-manrope text-[12px] font-bold text-white">
                            {tab === "closed"
                                ? "LTV filter only applies to Active loans"
                                : "Refine the visible rows"}
                        </div>
                    </div>
                    <div className="p-3 space-y-3">
                        <FilterSection
                            label="LTV band"
                            options={LTV_FILTERS}
                            value={filterLtv}
                            onChange={setFilterLtv}
                            disabled={tab === "closed"}
                        />
                        <div className="flex items-center justify-between border-t border-[#1D3E29]/10 pt-3">
                            <button
                                type="button"
                                disabled={!applied}
                                onClick={onClear}
                                className="text-[10px] font-semibold text-[#1D3E29]/70 hover:text-[#1D3E29] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Clear filters
                            </button>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="rounded-full bg-[#1D3E29] px-3 py-1 text-[10px] font-semibold text-white"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function FilterSection({ label, options, value, onChange, disabled = false }) {
    return (
        <div className={disabled ? "opacity-50" : ""}>
            <div className="text-[9px] font-bold uppercase tracking-widest text-[#1D3E29]/65">
                {label}
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
                {options.map((opt) => {
                    const active = value === opt.id;
                    return (
                        <button
                            key={opt.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => onChange(opt.id)}
                            className={`rounded-full px-2.5 py-0.5 text-[10.5px] font-semibold transition-colors ${
                                active
                                    ? "bg-[#1D3E29] text-[#FBC206]"
                                    : "bg-white text-black ring-1 ring-[#bfb67b] hover:bg-[#10281b]/5"
                            } disabled:cursor-not-allowed`}
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function LoanSortByMenu({ sortKey, setSortKey }) {
    const { open, setOpen, ref } = usePopover();
    const current = LOAN_SORT_OPTIONS.find((o) => o.id === sortKey) || LOAN_SORT_OPTIONS[0];
    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={open}
                className="inline-flex items-center gap-2 rounded-full bg-transparent px-3 py-1 text-[12px] font-semibold text-[#1D3E29] ring-1 ring-[#bfb67b] hover:bg-[#1D3E29]/5"
            >
                Sort: <span className="font-bold">{current.label}</span>
                <SortIcon />
            </button>

            {open ? (
                <div
                    role="menu"
                    className="absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden rounded-[12px] bg-white ring-1 ring-[#bfb67b] shadow-xl"
                >
                    <div className="bg-[#1D3E29] px-4 py-2">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            Sort By
                        </div>
                    </div>
                    <ul className="p-2">
                        {LOAN_SORT_OPTIONS.map((opt) => {
                            const active = opt.id === sortKey;
                            return (
                                <li key={opt.id}>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setSortKey(opt.id);
                                            setOpen(false);
                                        }}
                                        className={`flex w-full items-center justify-between rounded-[8px] px-3 py-1.5 text-left text-[11.5px] font-semibold transition-colors ${
                                            active
                                                ? "bg-[#1D3E29] text-white"
                                                : "text-[#1D3E29] hover:bg-[#10281b]/5"
                                        }`}
                                    >
                                        <span>{opt.label}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Loans table                                                               */
/* -------------------------------------------------------------------------- */

function LoansTable({ rows, isLoading, onRepay }) {
    return (
        <div className="overflow-hidden rounded-[10px]">
            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead>
                        <tr className="bg-[#10281b] text-[#e6c985]">
                            <Th>Bond ID/Name</Th>
                            <Th>Borrowed</Th>
                            <Th>Interest</Th>
                            <Th>Rem Capacity</Th>
                            <Th>LTV</Th>
                            <Th>Total Payable</Th>
                            <Th sortable={false}>Action</Th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {isLoading ? (
                            <tr>
                                <td colSpan="7" className="px-3 py-6 text-center text-[12px] text-black/60">
                                    Loading loans…
                                </td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="px-3 py-6 text-center text-[12px] text-black/60">
                                    No loans in this category yet.
                                </td>
                            </tr>
                        ) : (
                            rows.map((row) => <LoanRow key={row.id} row={row} onRepay={onRepay} />)
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Th({ children, sortable = true }) {
    return (
        <th className="px-3 py-3 text-left font-manrope text-[11px] font-semibold whitespace-nowrap">
            <span className="inline-flex items-center gap-1.5">
                {children}
                {sortable ? <SortHeadIcon /> : null}
            </span>
        </th>
    );
}

function LoanRow({ row, onRepay }) {
    const id = String(row.id).padStart(6, "0");
    const bondLabel = `#${id}/${row.txHash ? row.txHash.slice(2, 6).toUpperCase() : "D457"}...`;
    const borrowed = BigInt(row.loanBalance);
    const closed = Boolean(row.isClosedRow);

    // Interest column meaning differs by row kind: for active loans we
    // show the LIVE accrued interest (what the borrower owes right now,
    // sourced from the loan record's `accruedInterest` which the backend
    // refreshes per request); for closed loans we show what was actually
    // paid over the life of the loan.
    const interest = closed
        ? BigInt(row.totalInterestPaid || 0n)
        : BigInt(row.accruedInterest || 0n);

    // §V6.2 — per-product Safe Vault via rowSafeVault(). Standard 80%,
    // Turbo 50%. Was hardcoded to global 80% which mis-quoted Turbo
    // loans (e.g. $1001 Turbo would show $800 / $600 cap / 6% LTV
    // instead of the real $500.50 / $375.37 cap / 10% LTV).
    const safeVault = rowSafeVault(row);
    const maxBorrow = (safeVault * BigInt(config.protocol.maxLtvBps)) / 10000n;
    const rem = maxBorrow > borrowed ? maxBorrow - borrowed : 0n;
    const totalPayable = borrowed + interest;
    const ltvBps = !closed && safeVault > 0n ? Number((borrowed * 10000n) / safeVault) : 0;
    const ltvPct = Math.round(ltvBps / 100);
    // Loans liquidate only AFTER the term ends, so the badge stays green
    // until maturity, then turns orange (red in the last 5%).
    const termEnded = row.maturityTime > 0 && chainNowSec() >= row.maturityTime;

    return (
        <tr className="border-b border-black/10 last:border-0">
            <td className="px-3 py-3">
                <span className="font-manrope text-[12px] font-normal text-black">{bondLabel}</span>
                {closed ? (
                    <div className="text-[10px] text-black/50 mt-0.5">
                        {row.liquidatedAt ? "Liquidated" : "Repaid"}
                        {row.closedAt
                            ? ` • ${new Date(row.closedAt).toLocaleDateString()}`
                            : null}
                    </div>
                ) : null}
            </td>
            <td className="px-3 py-3 whitespace-nowrap font-manrope text-[12px] font-normal text-black">
                ${formatUsdcCompact(borrowed)} USDC
            </td>
            <td className="px-3 py-3 whitespace-nowrap font-manrope text-[12px] font-normal text-black">
                ${formatUsdcCompact(interest)} USDC
            </td>
            <td className="px-3 py-3 whitespace-nowrap font-manrope text-[12px] font-normal text-black">
                {closed ? "—" : `$${formatUsdcCompact(rem)} USDC`}
            </td>
            <td className="px-3 py-3">
                {closed ? (
                    <span className="inline-flex items-center rounded-full bg-[#1D3E29]/10 px-2.5 py-0.5 text-[10px] font-semibold text-[#1D3E29]">
                        Closed
                    </span>
                ) : (
                    <LtvBadge ltv={ltvPct} termEnded={termEnded} />
                )}
            </td>
            <td className="px-3 py-3 whitespace-nowrap font-manrope text-[12px] font-normal text-black">
                ${formatUsdcCompact(totalPayable)} USDC
            </td>
            <td className="px-3 py-3">
                <div className="flex items-center gap-2">
                    {closed ? null : (
                        <button
                            type="button"
                            disabled={!onRepay || borrowed === 0n}
                            onClick={() => onRepay && onRepay(row)}
                            className="inline-flex items-center gap-1 rounded-full bg-[#1D3E29] px-3 py-1 text-[11px] font-semibold text-white hover:bg-[#0E2C19] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Pay Now
                            <ChevronRight />
                        </button>
                    )}
                    <Link
                        to={`/loans/${row.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-[#1D3E29] px-3 py-1 text-[11px] font-semibold text-[#1D3E29] hover:bg-[#1D3E29] hover:text-white transition-colors"
                    >
                        View More
                        <ChevronRight />
                    </Link>
                </div>
            </td>
        </tr>
    );
}

/* -------------------------------------------------------------------------- */
/*  Icons                                                                     */
/* -------------------------------------------------------------------------- */

function DollarIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="12" y1="3" x2="12" y2="21" />
            <path d="M16 7H10a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6H8" />
        </svg>
    );
}

function CoinIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v10" />
            <path d="M9.5 9.5h4a1.8 1.8 0 0 1 0 3.5h-3a1.8 1.8 0 0 0 0 3.5h4" />
        </svg>
    );
}

function ShieldIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3 L20 6 V12 C20 17 16 21 12 22 C8 21 4 17 4 12 V6 Z" />
            <polyline points="9 12 11 14 15 10" />
        </svg>
    );
}

function StackIcon() {
    return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="12 3 21 8 12 13 3 8 12 3" />
            <polyline points="3 13 12 18 21 13" />
        </svg>
    );
}

function SortHeadIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="3 5 6 2 9 5" />
            <polyline points="3 7 6 10 9 7" />
        </svg>
    );
}

function SearchIcon() {
    return (
        <svg className="h-4 w-4 text-[#1D3E29]" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="9" cy="9" r="6" />
            <line x1="14" y1="14" x2="18" y2="18" />
        </svg>
    );
}

function FiltersIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="2" y1="4" x2="14" y2="4" />
            <line x1="4" y1="8" x2="12" y2="8" />
            <line x1="6" y1="12" x2="10" y2="12" />
        </svg>
    );
}

function SortIcon() {
    return (
        <svg className="h-3.5 w-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="3" y1="5" x2="13" y2="5" />
            <line x1="5" y1="8" x2="11" y2="8" />
            <line x1="7" y1="11" x2="9" y2="11" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}
